'use client';

import { useEffect, useMemo, useState } from 'react';
import { defaultBasketData } from 'constant';
import {
  useGetShoppingCartQuery,
  useRemoveGiftCardPaymentMutation
} from 'services/shoppingCartApi';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  removeFromLocalCart,
  updateLocalCartQuantity
} from 'store/reducers/cartSlice';

import { formatCurrency } from 'utils';

import { ShopCartItem, ShopCartType } from 'types';

import { Button, Counter, Divider } from 'ui';

interface PackagesDetailsItemProps {
  item: ShopCartItem;
  deleteShoppingCartItem: (params: {
    invoiceId: number;
    cartItemId: number;
  }) => Promise<unknown>;
  updateShoppingCartItem: (params: {
    invoiceId: number;
    cartItemId: number;
    itemId: number;
    itemType: ShopCartType;
    qty: number;
  }) => Promise<unknown>;
}

const PackagesDetailsItem = ({
  item,
  deleteShoppingCartItem,
  updateShoppingCartItem
}: PackagesDetailsItemProps) => {
  const {
    colors: {
      palette: {
        shop: {
          packages: {
            stepCartDetails: {
              list: { item: itemColors }
            }
          }
        }
      }
    }
  } = useTheme();

  const [count, setCount] = useState<number>(0);
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { selectedPackages } = useAppSelector((state) => state.cart);

  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);

  const { data = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isLogged
  });
  const [removeGiftCardPayment] = useRemoveGiftCardPaymentMutation();

  const removeGiftCardPaymentHandler = () => {
    if (data.payments.length > 0) {
      const pymtIds = data.payments.map((payment) => payment?.in_pymt_id);
      const deletePromises = pymtIds.map((id) =>
        removeGiftCardPayment({
          in_pymt_id: id,
          invoice_id: data.details.invoice_id ?? 0
        })
      );

      return Promise.allSettled(deletePromises);
    }
  };

  const isDiscountExist = item.base_discount < 0;

  const existingShoppingCartItem = useMemo(
    () => data.items.find((shopItem) => shopItem.id === item.id),
    [data.items, item]
  );

  const existingLocalCartItem = useMemo(
    () => selectedPackages.find((shopItem) => shopItem.id === item.id),
    [selectedPackages, item]
  );

  useEffect(() => {
    if (isLogged) {
      setCount(existingShoppingCartItem?.qty ?? 0);
    } else {
      setCount(existingLocalCartItem?.qty ?? 0);
    }
  }, [existingShoppingCartItem, existingLocalCartItem, isLogged]);

  const addOnlineCartCount = (quantity: number) => {
    if (quantity < 1) {
      setCount(quantity);
    } else {
      updateShoppingCartItem({
        invoiceId: data.details.invoice_id ?? 0,
        cartItemId: existingShoppingCartItem?.cart_item_id ?? 0,
        itemId: existingShoppingCartItem?.id ?? 0,
        itemType: existingShoppingCartItem?.item_type ?? ShopCartType.Package,
        qty: quantity
      })
        .then(() => setCount(quantity))
        .then(removeGiftCardPaymentHandler);
    }
  };

  const onOnlineDelete = () =>
    deleteShoppingCartItem({
      invoiceId: data.details.invoice_id ?? 0,
      cartItemId: existingShoppingCartItem?.cart_item_id ?? 0
    })
      .then(() => setCount(0))
      .then(removeGiftCardPaymentHandler);

  const addLocalCartCount = (quantity: number) => {
    if (quantity > 1) {
      dispatch(
        updateLocalCartQuantity({
          type: ShopCartType.Package,
          id: existingLocalCartItem?.id ?? 0,
          qty: quantity
        })
      );
    }
    setCount(quantity);
  };

  const onLocalDelete = () =>
    dispatch(
      removeFromLocalCart({
        type: ShopCartType.Package,
        id: existingLocalCartItem?.id ?? 0
      })
    );

  const onUpdate = isLogged ? addOnlineCartCount : addLocalCartCount;
  const onDelete = isLogged ? onOnlineDelete : onLocalDelete;

  return (
    <Box>
      <Divider bg={itemColors.divider} height='1px' border='none' />
      <Grid
        templateColumns='repeat(12, 1fr)'
        templateRows={{ base: 'repeat(4, auto)', lg: 'repeat(2, auto)' }}
        py={{ base: '20px', md: '24px' }}
        columnGap='10px'
        rowGap='5px'
      >
        <GridItem colSpan={{ base: 8, lg: 5 }} rowStart={1}>
          <Stack
            gap={0}
            h='100%'
            w='100%'
            justifyContent='center'
            pr={{ base: '20px', lg: 0 }}
          >
            <Text
              fontSize={['20px', '23px', '25px']}
              fontWeight='regular'
              color={itemColors.text}
            >
              {item.name}
            </Text>
          </Stack>
        </GridItem>

        {isDiscountExist && (
          <GridItem
            display={{ base: 'none', lg: 'block' }}
            colSpan={5}
            rowStart={2}
          >
            <Stack gap={0} height='100%' justifyContent='center'>
              <Text
                fontSize='12px'
                color={itemColors.discount}
                textTransform='uppercase'
              >
                Discount
              </Text>
            </Stack>
          </GridItem>
        )}

        <GridItem
          colSpan={{ base: 12, lg: 2 }}
          rowStart={{ base: 2, lg: 'auto' }}
          mt={{ base: '15px', lg: '0' }}
          overflow='hidden'
        >
          <Divider
            display={{ base: 'block', lg: 'none' }}
            top={0}
            opacity={0.3}
            bg={itemColors.amountDivider}
            height='1px'
            border='none'
          />

          <Stack
            pt={{ base: '20px', lg: '0' }}
            gap={0}
            flexDirection={{ base: 'row', lg: 'column' }}
            h='100%'
            w='100%'
            justifyContent={{ base: 'space-between', lg: 'center' }}
            alignItems={{ base: 'center', lg: 'flex-end' }}
          >
            <Text
              display={{ base: 'block', lg: 'none' }}
              fontSize='12px'
              color={itemColors.amountLabel}
              textTransform='uppercase'
            >
              Amount
            </Text>

            <Text
              fontSize={['20px', '23px', '25px']}
              color={itemColors.amount}
              textAlign='end'
            >
              {formatCurrency(item.amount)}
            </Text>
          </Stack>
        </GridItem>

        {isDiscountExist && (
          <GridItem colSpan={{ base: 12, lg: 2 }} rowStart={{ base: 3, lg: 2 }}>
            <Stack
              gap={0}
              mt={{ base: '5px', lg: '0' }}
              flexDirection={{ base: 'row', lg: 'column' }}
              h='100%'
              w='100%'
              justifyContent={{ base: 'space-between', lg: 'center' }}
              alignItems={{ base: 'center', lg: 'flex-end' }}
            >
              <Text
                fontSize='12px'
                color={itemColors.discount}
                textTransform='uppercase'
                display={{ base: 'block', lg: 'none' }}
              >
                Discount
              </Text>
              <Text
                fontSize={{ base: '15px', md: '18px', lg: '21px' }}
                color={itemColors.discount}
                textAlign='end'
              >
                {formatCurrency(item.base_discount)}
              </Text>
            </Stack>
          </GridItem>
        )}

        <GridItem
          colSpan={{ base: 4, lg: 3 }}
          rowSpan={{ base: 1, lg: isDiscountExist ? 2 : 1 }}
          rowStart={{ base: 1, lg: 'auto' }}
        >
          <Flex
            justifyContent={{ base: 'flex-end', lg: 'center' }}
            alignItems='center'
            height='100%'
            minH='45px'
          >
            <Counter
              isAlternativeDesign
              value={count || 0}
              onChange={onUpdate}
            />
          </Flex>
        </GridItem>

        {count === 0 ? (
          <GridItem
            colSpan={{ base: 12, lg: 2 }}
            rowSpan={{ base: 1, lg: 2 }}
            rowStart={{ base: 4, lg: 'auto' }}
          >
            <Stack
              gap={0}
              h='100%'
              w='100%'
              justifyContent='center'
              alignItems='flex-end'
              mt={{ base: '10px', lg: '0' }}
            >
              <Button colorScheme='red' w='130px' h='45px' onClick={onDelete}>
                Delete
              </Button>
            </Stack>
          </GridItem>
        ) : (
          <GridItem
            colSpan={{ base: 12, lg: 2 }}
            rowStart={{ base: 4, lg: 'auto' }}
          >
            <Stack
              gap={0}
              h='100%'
              w='100%'
              mt={{ base: '10px', lg: '0' }}
              justifyContent={{ base: 'space-between', lg: 'center' }}
              alignItems={{ base: 'center', lg: 'flex-end' }}
              flexDirection={{ base: 'row', lg: 'column' }}
            >
              <Text
                display={{ base: 'block', lg: 'none' }}
                fontSize='12px'
                color={itemColors.totalLabel}
                textTransform='uppercase'
              >
                Total
              </Text>

              <Text
                fontSize={['20px', '23px', '25px']}
                color={itemColors.total}
                textAlign='end'
              >
                {formatCurrency(item.amount_total)}
              </Text>
            </Stack>
          </GridItem>
        )}
      </Grid>
    </Box>
  );
};

export default PackagesDetailsItem;
